import {useEffect, useRef, useState} from 'react'
import {Banner} from '@primer/react'
import {useAnalytics} from '@github-ui/use-analytics'
import type {Condition, RepositoryIdParameters, SourceType} from '../types/rules-types'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import {propertyDefinitionSettingsPath} from '@github-ui/paths'
import type {Repository} from '@github-ui/current-repository'

const REPO_IDS_BANNER_THRESHOLD = 5

interface Props {
  conditions: Condition[]
  sourceType: SourceType
  source: Repository | Organization | Enterprise
}

function getRepositoryIdCount(conditions: Condition[]): number {
  const repoIdCondition = conditions.find(c => c.target === 'repository_id')
  if (!repoIdCondition) return 0
  return (repoIdCondition.parameters as RepositoryIdParameters).repository_ids.length
}

export function CreatePropertyBanner({conditions, source, sourceType}: Props) {
  const hasTrackedShownEventRef = useRef(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const repoIdCount = getRepositoryIdCount(conditions)
  const shouldShowBanner = sourceType === 'organization' && repoIdCount > REPO_IDS_BANNER_THRESHOLD

  useEffect(() => {
    if (!shouldShowBanner) return
    if (hasTrackedShownEventRef.current) return
    hasTrackedShownEventRef.current = true
    sendAnalyticsEvent('repo_custom_properties_suggestions.ruleset_banner.shown')
  }, [sendAnalyticsEvent, shouldShowBanner])

  if (!shouldShowBanner || isDismissed) return null

  const path = propertyDefinitionSettingsPath({pathPrefix: 'organizations', sourceName: source.name})

  return (
    <Banner
      onDismiss={() => {
        sendAnalyticsEvent('repo_custom_properties_suggestions.ruleset_banner.dismissed')
        setIsDismissed(true)
      }}
      title="Automate targeting with custom properties"
      description={`You've manually selected ${repoIdCount} repositories. A custom property lets you target them as a group and keeps the list up to date as repositories change.`}
      variant="info"
      primaryAction={
        <Banner.PrimaryAction
          as="a"
          rel="noopener noreferrer"
          target="_blank"
          href={path}
          onClick={() => sendAnalyticsEvent('repo_custom_properties_suggestions.ruleset_banner.clicked')}
        >
          Create property
        </Banner.PrimaryAction>
      }
    />
  )
}
